// Direktori Mitra Terdekat (mig 4230) — helper MURNI untuk halaman publik
// `/cari-mitra`. Pembeli umum memilih kecamatannya, lalu melihat mitra yang
// alamat UTAMA-nya berdekatan dan order lewat WhatsApp/katalog mereka.
//
// Sengaja BEBAS dari Supabase/React/fetch supaya gampang diuji — idiom
// `resellerCatalog.ts` & `mitra.ts`. Suite Vitest berjalan `environment: node`
// TANPA mock Supabase (konvensi, bukan kebetulan), jadi apa pun yang menyentuh
// jaringan tidak boleh mendarat di sini.
//
// ⚠️ NOMOR WHATSAPP TIDAK PERNAH LEWAT MODUL INI. Payload daftar hanya membawa
// `slug`; nomornya diambil satu-per-satu oleh route redirect `/api/mitra/wa`
// dari `get_public_reseller_catalog`. Itulah yang membuat direktori ini tetap
// menghormati keputusan anti-enumerasi mig 2160 — lihat kepala migrasi 4230.

import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
import {
  formatCityLabel,
  formatDistrictLabel,
  formatProvinceLabel,
} from "@/lib/regionLabel";
import { buildCatalogPath, displayResellerName } from "@/lib/resellerCatalog";

/**
 * Peringkat kedekatan; 0 paling dekat. Cermin `proximity` di mig 4230.
 * Peringkat 3 ("Mitra lainnya") tak lagi dikirim sejak mig 8540 dan dibuang
 * parser bila payload lama masih membawanya.
 */
export type PartnerProximity = 0 | 1 | 2;

export type PartnerCard = {
  /** Judul kartu: nama toko, jatuh ke nama lengkap bila kosong. */
  name: string;
  /** Baris kedua: nama pelanggan. "" = jangan dirender (lihat partnerPerson). */
  person: string;
  district: string;
  city: string;
  province: string;
  /** `customers.catalog_slug` — satu-satunya pengenal yang keluar ke publik. */
  slug: string;
  proximity: PartnerProximity;
};

export type PartnerArea = {
  district: string;
  city: string;
  province: string;
};

export type PartnerDirectoryResult =
  | { status: "off" }
  | { status: "unknown-area" }
  | { status: "no-partners" }
  | { status: "no-match"; area: PartnerArea; catalogEnabled: boolean }
  | {
      status: "ok";
      area: PartnerArea;
      catalogEnabled: boolean;
      cards: PartnerCard[];
    };

/** Keadaan layar. SATU pemetaan — dipakai halaman DAN teks kosongnya. */
export type DirectoryState = PartnerDirectoryResult["status"];

// ─── Normalisasi ──────────────────────────────────────────────────────────────

function tidy(raw: unknown): string {
  return typeof raw === "string" ? raw.trim().replace(/\s+/g, " ") : "";
}

/**
 * Kunci banding wilayah: huruf kecil, spasi dirapatkan.
 *
 * Kembaran TS dari `lower(btrim(...))` di mig 4230. Ia SENGAJA tidak melepas
 * penanda "Kota "/"Kab. " seperti `classifyCity`: kedua sisi pencocokan berasal
 * dari direktori `biteship_areas` yang sama (form alamat hanya mengizinkan
 * kota/provinsi terisi lewat DistrictPicker), dan 0 dari 2.759 baris direktori
 * itu memuat penanda jenis. Melepas yang tak pernah ada hanya menambah satu
 * salinan aturan yang bisa menyimpang dari SQL.
 */
export function normalizeRegionKey(raw: unknown): string {
  return foldText(raw);
}

/** Lipatan banding umum: rapikan spasi lalu huruf kecil. */
function foldText(raw: unknown): string {
  return tidy(raw).toLowerCase();
}

// ─── Nama & wilayah untuk kartu ───────────────────────────────────────────────

/** Judul kartu. Delegasi `displayResellerName` — satu aturan, satu tempat. */
export function partnerTitle(
  storeName: string | null | undefined,
  fullName: string
): string {
  return displayResellerName(storeName, fullName);
}

/**
 * Baris kedua kartu. "" berarti JANGAN dirender.
 *
 * Kosong dalam dua keadaan: tak ada nama toko (judul sudah memakai nama
 * lengkap — 821 dari 886 pelanggan PROD ada di jalur ini, jadi ini bukan kasus
 * pinggir), atau nama toko dan nama orangnya memang sama. Tanpa gerbang ini
 * kartu menulis baris kembar.
 */
export function partnerPerson(
  storeName: string | null | undefined,
  fullName: string
): string {
  const store = tidy(storeName);
  const person = tidy(fullName);
  if (store === "" || person === "") return "";
  return foldText(store) === foldText(person) ? "" : person;
}

/**
 * Satu baris wilayah: "Kec. Bekasi Timur · Kab./Kota Bekasi · Jawa Barat".
 *
 * Bagian kosong DIBUANG, bukan disisakan sebagai pemisah menggantung — cermin
 * `formatAreaSecondaryLine`. Penulisan awalannya diserahkan penuh ke
 * `regionLabel.ts`: modul itu satu-satunya yang boleh memutuskannya, dan ia yang
 * tahu jebakan "Kotawaringin Timur".
 */
export function partnerRegionLine(card: {
  district?: string | null;
  city?: string | null;
  province?: string | null;
}): string {
  return [
    formatDistrictLabel(card.district),
    formatCityLabel(card.city),
    formatProvinceLabel(card.province),
  ]
    .filter((part) => part !== "")
    .join(" · ");
}

// ─── Peringkat ────────────────────────────────────────────────────────────────

const PROXIMITY_LABEL: Record<PartnerProximity, string> = {
  0: "Di kecamatanmu",
  1: "Di kota/kabupatenmu",
  2: "Di provinsimu",
};

export function proximityLabel(proximity: PartnerProximity): string {
  return PROXIMITY_LABEL[proximity];
}

export type PartnerSection = {
  proximity: PartnerProximity;
  label: string;
  cards: PartnerCard[];
};

/**
 * Kartu → seksi berlabel, urut peringkat menaik. Seksi kosong DIBUANG.
 *
 * Pengelompokan ini bukan hiasan: sebaran mitra panjang-ekor (mayoritas kota
 * hanya punya 1 mitra), jadi tanpa seksi, hasil "kota sama" dan "provinsi sama"
 * bercampur dan pembeli tak tahu mana yang benar-benar dekat.
 */
export function groupByProximity(cards: PartnerCard[]): PartnerSection[] {
  const order: PartnerProximity[] = [0, 1, 2];
  return order
    .map((proximity) => ({
      proximity,
      label: PROXIMITY_LABEL[proximity],
      cards: cards.filter((card) => card.proximity === proximity),
    }))
    .filter((section) => section.cards.length > 0);
}

// ─── Parsing payload RPC ──────────────────────────────────────────────────────

function isProximity(value: unknown): value is PartnerProximity {
  return value === 0 || value === 1 || value === 2;
}

function parseCard(raw: unknown): PartnerCard | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const slug = tidy(row.slug).toLowerCase();
  // Kartu tanpa slug tak punya tombol apa pun — buang, jangan render mati.
  if (slug === "") return null;
  const proximity = typeof row.proximity === "number" ? row.proximity : NaN;
  if (!isProximity(proximity)) return null;
  const name = tidy(row.name);
  if (name === "") return null;
  return {
    name,
    // SQL memulangkan `person` hanya bila store_name terisi, jadi di sini `name`
    // pasti nama toko — aturan dedup yang sama dengan partnerPerson berlaku.
    person: partnerPerson(name, tidy(row.person)),
    district: tidy(row.district),
    city: tidy(row.city),
    province: tidy(row.province),
    slug,
    proximity,
  };
}

function parseArea(raw: unknown): PartnerArea | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const city = tidy(row.city);
  const province = tidy(row.province);
  if (city === "" && province === "") return null;
  return { district: tidy(row.district), city, province };
}

/**
 * jsonb `search_public_partners` → bentuk aman. TIDAK PERNAH melempar.
 *
 * Hanya field yang disebut di sini yang lolos, jadi kolom sensitif yang suatu
 * hari keliru ditambahkan di SQL (phone/address_line) tetap tak sampai ke layar.
 * Itu lapisan kedua; lapisan pertamanya adalah payload RPC yang memang tak
 * memuatnya.
 */
export function parsePartnerDirectoryPayload(
  raw: unknown
): PartnerDirectoryResult {
  if (!raw || typeof raw !== "object") return { status: "off" };
  const body = raw as Record<string, unknown>;
  if (body.disabled === true) return { status: "off" };
  if (body.area_found !== true) return { status: "unknown-area" };
  if (body.has_partners !== true) return { status: "no-partners" };

  const area = parseArea(body.area);
  if (!area) return { status: "unknown-area" };
  const catalogEnabled = body.catalog_enabled !== false;

  const items = Array.isArray(body.items) ? body.items : [];
  const cards = items
    .map(parseCard)
    .filter((card): card is PartnerCard => card !== null);

  return cards.length === 0
    ? { status: "no-match", area, catalogEnabled }
    : { status: "ok", area, catalogEnabled, cards };
}

export function directoryState(result: PartnerDirectoryResult): DirectoryState {
  return result.status;
}

// ─── Kartu yang dirender (mig 8540) ───────────────────────────────────────────

/**
 * Kartu yang DIRENDER, putusan pemilik 2026-10-23: SATU mitra tingkat atas —
 * kecamatan bila ada, kalau tidak kota — diundi tiap kunjungan, lalu semua
 * kartu provinsi apa adanya (urutannya tetap dari seed 5 menit RPC).
 *
 * RPC mig 8540 mengirim KOLAM calon tingkat atas, bukan satu kartu: fungsinya
 * `stable` dan hasilnya di-cache per bucket seed, jadi undian per kunjungan
 * hanya bisa terjadi di sini. Aturan tingkatnya ikut ditegakkan di sini
 * (kecamatan terisi → kartu kota dibuang) supaya layar tetap benar walau
 * payload lama membawa keduanya.
 *
 * `random` ∈ [0, 1) disuntik — halaman memberi `Math.random()` — supaya
 * undiannya bisa diuji. Nilai di luar rentang dijepit, NaN dianggap 0.
 */
export function pickVisiblePartners(
  cards: PartnerCard[],
  random: number
): PartnerCard[] {
  const district = cards.filter((card) => card.proximity === 0);
  const pool =
    district.length > 0 ? district : cards.filter((card) => card.proximity === 1);
  const province = cards.filter((card) => card.proximity === 2);
  if (pool.length === 0) return province;
  const r = Number.isFinite(random) ? random : 0;
  const index = Math.min(pool.length - 1, Math.max(0, Math.floor(r * pool.length)));
  return [pool[index], ...province];
}

/** `pickVisiblePartners` atas hasil utuh: kartu habis → "no-match". */
export function pickVisibleDirectory(
  result: PartnerDirectoryResult,
  random: number
): PartnerDirectoryResult {
  if (result.status !== "ok") return result;
  const cards = pickVisiblePartners(result.cards, random);
  return cards.length === 0
    ? { status: "no-match", area: result.area, catalogEnabled: result.catalogEnabled }
    : { ...result, cards };
}

// ─── Tautan ───────────────────────────────────────────────────────────────────

/** `area_id` Biteship, mis. "IDNP10IDNC243IDND2491". Gerbang murah sebelum RPC. */
export function isValidAreaId(raw: string | null | undefined): boolean {
  return /^ID[A-Z0-9]{6,60}$/.test(tidy(raw));
}

/** `/cari-mitra?area=…(&produk=…)` — URL yang bisa dibagikan pembeli. */
export function partnerDirectoryPath(
  areaId: string | null | undefined,
  productSlug?: string | null
): string {
  const params = new URLSearchParams();
  const area = tidy(areaId);
  if (area !== "") params.set("area", area);
  const produk = tidy(productSlug);
  if (produk !== "") params.set("produk", produk);
  const qs = params.toString();
  return qs === "" ? "/cari-mitra" : `/cari-mitra?${qs}`;
}

/**
 * Tombol WhatsApp → route redirect, BUKAN `wa.me` langsung.
 *
 * Dua alasan, keduanya mengikat. (1) Nomornya tak pernah sampai ke klien, jadi
 * satu halaman hasil tak bisa dipanen jadi 24 nomor. (2) `window.open` sesudah
 * `await` diblokir popup blocker iOS Safari; `<a target="_blank">` yang di-302
 * adalah navigasi biasa dan selalu lolos.
 */
export function partnerWaHref(
  slug: string,
  productSlug?: string | null
): string {
  const params = new URLSearchParams({ slug: tidy(slug) });
  const produk = tidy(productSlug);
  if (produk !== "") params.set("produk", produk);
  return `/api/mitra/wa?${params.toString()}`;
}

/** Tombol Lihat Katalog → route redirect (hitungan klik), tujuan `kelolify.com/toko/<slug>` (mig 8936). */
export function partnerCatalogHref(slug: string): string {
  return `/api/mitra/katalog?slug=${encodeURIComponent(tidy(slug))}`;
}

/** PATH tujuan tombol katalog (origin-nya `catalogShareOrigin`) — delegasi, jangan tulis ulang bentuk pathnya. */
export function partnerCatalogTarget(slug: string): string {
  return buildCatalogPath(tidy(slug));
}

// ─── Pesan WhatsApp ───────────────────────────────────────────────────────────

export type PartnerWaContext = {
  partnerName: string;
  areaLabel?: string | null;
  storeName?: string | null;
  /** Konteks produk (fitur B). Kosong = pesan umum. */
  product?: { name: string; url?: string | null } | null;
};

/**
 * Pesan pembuka chat. Menyebut produk bila pembeli datang dari halaman produk —
 * itu yang mengubah direktori jadi jalur order; tanpa konteks, pembeli membuka
 * chat kosong dan sering berhenti di situ.
 */
export function buildPartnerWaMessage(
  ctx: PartnerWaContext,
  overrides?: WaTemplateOverrides | null
): string {
  const store = tidy(ctx.storeName);
  const area = tidy(ctx.areaLabel);
  const productName = tidy(ctx.product?.name);
  const productUrl = tidy(ctx.product?.url);

  return renderWaTemplate(resolveWaTemplate("mitra_direktori", overrides), {
    "[nama mitra]": tidy(ctx.partnerName) || "Kak",
    "[baris asal daftar]":
      store === ""
        ? "di daftar mitra terdekat."
        : `di daftar mitra terdekat ${store}.`,
    "[baris wilayah]": area === "" ? "" : `Saya di ${area}.`,
    "[blok produk]":
      productName === ""
        ? ""
        : [`Saya mau tanya produk ini: ${productName}`, productUrl]
            .filter(Boolean)
            .join("\n")
  });
}

// ─── Urutan acak berotasi (fitur C) ───────────────────────────────────────────

/** Panjang bucket seed, ms. 5 menit = 288 rotasi/hari. */
export const DIRECTORY_SEED_BUCKET_MS = 5 * 60_000;

/**
 * Seed pengacak urutan, dioper ke `search_public_partners` sebagai `p_seed`.
 *
 * Bucket 5 menit, bukan nilai acak per-request, karena dua alasan sekaligus:
 * `random()` tak boleh dipakai di fungsi SQL `stable` (syarat PostgREST GET),
 * dan seed yang berubah tiap request akan membatalkan cache `next.revalidate`
 * setiap kali. Dengan bucket, urutan tetap berotasi 288×/hari sementara cache
 * ter-partisi rapi per bucket.
 *
 * Kenapa diacak sama sekali: di Bekasi ada 14 mitra sekota — tanpa rotasi, yang
 * kebetulan terurut pertama memanen hampir semua chat, permanen, dan opt-in jadi
 * tak menarik bagi yang lain.
 */
export function directorySeed(nowMs: number): string {
  const safe = Number.isFinite(nowMs) ? nowMs : 0;
  return String(Math.floor(safe / DIRECTORY_SEED_BUCKET_MS));
}

// ─── Statistik mitra (fitur D) ────────────────────────────────────────────────
// Kartu /account TIDAK lagi membaca `partner_directory_self_stats` sejak mig
// 6210: statistik direktori digantikan statistik "Website Toko Saya"
// (`src/lib/resellerCatalogStats.ts`), yang tetap membawa klik WhatsApp
// direktori sebagai angka "Chat WA". Pencatat direktori (impresi & klik) tetap
// hidup — ia juga mengisi Laporan Direktori Mitra di admin.

/** Gerbang tombol opt-in. Pesan SAMA dengan yang ditegakkan RPC di server. */
export function canListAsPartner(input: {
  status: string | null | undefined;
  hasPrimaryAddress: boolean;
}): string | null {
  if (tidy(input.status).toLowerCase() !== "active") {
    return "Akunmu belum aktif.";
  }
  if (!input.hasPrimaryAddress) {
    return "Isi alamat utama kamu dulu di bagian Alamat.";
  }
  return null;
}

/**
 * Tombol "Order Melalui Mitra Terdekat" tampil di halaman produk?
 *
 * Empat gerbang, dan tiga di antaranya menutup:
 *
 * 1. `catalogMode` — di dalam katalog reseller white-label tombol ini
 *    mengirim pembeli SEORANG RESELLER untuk mencari reseller lain;
 *    kebocoran yang merugikan pemilik katalognya sendiri.
 * 2. `settingEnabled` — saklar pemilik toko (`partner_directory_enabled` DAN
 *    `partner_product_cta_enabled`, digabung di `usePartnerProductCta`).
 * 3. `customerLoading` — sesi pembeli BELUM diketahui. Fail-closed: menebak
 *    "tamu" lebih dulu membuat tombol berkedip muncul-lalu-hilang di mata
 *    pembeli yang sesungguhnya sudah login. Pola sama dengan
 *    `showGuestWaOrder` (guestWaOrder.ts).
 * 4. `isAuthenticated` — pembeli yang punya akun memesan LANGSUNG di toko
 *    (harga grupnya, keranjang, nota); mengalihkannya ke mitra justru
 *    melepas order yang sudah di tangan. Direktori mitra adalah jalan masuk
 *    untuk pengunjung yang belum jadi pelanggan.
 */
export function showPartnerProductCta(input: {
  catalogMode: boolean;
  settingEnabled: boolean;
  customerLoading: boolean;
  isAuthenticated: boolean;
}): boolean {
  if (input.catalogMode) return false;
  if (!input.settingEnabled) return false;
  if (input.customerLoading) return false;
  return !input.isAuthenticated;
}
